import { useRef, useState } from 'react';
import { Command } from 'cmdk';
import { Popover } from 'radix-ui';
import { Plus } from 'lucide-react';
import type { Project, Task } from '@timato/shared';
import { Input } from '@/components/ui';
import { TaskOption, taskOptionClass, useTaskSuggestions } from '@/components/tasks';
import { useProjectMap } from '@/data/hooks';

type Props = {
  id: string; value: string; onChange: (text: string) => void;
  /** The dialog's project: its tasks lead the list, as the chip's do on the timer. */
  project: Project | null;
  /** A suggestion was picked: the session belongs to that task, not merely to its name. */
  onPick: (task: Task) => void;
  autoFocus?: boolean;
};

/**
 * The goal of a session added or edited by hand (TW-5): a text field that suggests open tasks, the timer's list (§3.2)
 * under a plain input. Empty, it offers the project's open tasks as soon as it has focus, most recently worked first
 * (every project's with none chosen); typing narrows that to matches from any project, the project's first. Free text
 * stays free, so the first row keeps what was typed and Enter on it only closes the list; with the list closed, Enter
 * saves the dialog as any field does.
 *
 * cmdk drives the list and the arrow keys from its root, around our own input: its `Command.Input` insists on its own
 * id and label, which would cut the field loose from the "Goal" label above it.
 */
export function GoalField({ id, value, onChange, project, onPick, autoFocus }: Props) {
  const projects = useProjectMap();
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const typed = value.trim();
  const suggestions = useTaskSuggestions(typed, project?.id ?? null);
  const { inProject } = suggestions;
  // Nothing typed yet is a browse of the project, so another project's task has no business in it.
  const browsing = !typed && !!project;
  const matches = browsing ? suggestions.matches.filter(inProject) : suggestions.matches;
  const found = browsing ? suggestions.found.filter(inProject) : suggestions.found;
  // What was typed is its own row unless this project already has a task by that name, which is the same thing.
  // A namesake elsewhere is not: picking it would move the session to that project.
  const exact = !typed || matches.some((t) => inProject(t) && t.name.trim().toLowerCase() === typed.toLowerCase());
  const shown = open && matches.length > 0;
  // The highlighted row, held here rather than by cmdk: its pick from the browse list would survive typing, and Enter
  // would then take a task where the text was meant. Typing clears it, so the first row (what was typed) leads again.
  const [active, setActive] = useState('');
  const rows = [...(exact ? [] : [TYPED]), ...matches.map((t) => t.id)];
  const selected = rows.includes(active) ? active : rows[0] ?? '';
  const listId = `${id}-suggestions`;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && !shown) setOpen(true);
    // cmdk takes Enter for its list; closed, it belongs to the form again.
    if (e.key === 'Enter' && !shown) { e.preventDefault(); input.current?.form?.requestSubmit(); }
  };

  return (
    <Command shouldFilter={false} loop label="Goal suggestions" value={selected} onValueChange={setActive} onKeyDown={onKeyDown} className="flex-1 min-w-0">
      <Popover.Root open={shown} onOpenChange={setOpen}>
        <Popover.Anchor asChild>
          {/* Leaving the field ends the list, or a task made by Save would pop it open on the way out. */}
          <Input ref={input} id={id} autoFocus={autoFocus} value={value} onChange={(e) => { onChange(e.target.value); setActive(''); setOpen(true); }} onBlur={() => setOpen(false)}
            // An empty field suggests the moment it has focus. A filled one (an edit, a kept draft) waits for a keypress
            // or a click: a list over a goal already written is in the way.
            onFocus={() => { if (!typed) setOpen(true); }} onClick={() => setOpen(true)}
            placeholder="What was it?" autoComplete="off" role="combobox" aria-autocomplete="list" aria-expanded={shown} aria-controls={shown ? listId : undefined}
            className="h-10 text-[13px]" />
        </Popover.Anchor>
        <Popover.Portal>
          {/* Focus stays in the field: the list is read with the arrow keys, and a click must not blur the input first.
              Not a dialog, which is Radix's default role: the listbox inside is what the combobox points at. */}
          <Popover.Content role="presentation" align="start" sideOffset={6} onOpenAutoFocus={(e) => e.preventDefault()} onMouseDown={(e) => e.preventDefault()}
            onInteractOutside={(e) => { if (input.current?.contains(e.target as Node)) e.preventDefault(); }}
            className="z-50 w-[var(--radix-popover-trigger-width)] min-w-[280px] max-w-[calc(100vw-32px)] rounded-input border border-border bg-card shadow-popover overflow-hidden outline-none">
            <Command.List id={listId} className="max-h-[280px] overflow-y-auto py-1">
              {!exact && (
                <Command.Item value={TYPED} onSelect={() => setOpen(false)} className={taskOptionClass}>
                  <span className="w-[18px] h-[18px] rounded-chip bg-inset text-ink-2 grid place-items-center shrink-0"><Plus size={12} strokeWidth={3} /></span>
                  <span className="flex-1 truncate">Use <b>“{typed}”</b></span>
                  <span className="text-[11px] font-semibold text-ink-2 shrink-0">{project ? `new task in ${project.name}` : 'as typed'}</span>
                </Command.Item>
              )}
              {matches.map((t) => <TaskOption key={t.id} task={t} project={projects.get(t.projectId)} onSelect={() => { onPick(t); setOpen(false); }} />)}
              {found.length > matches.length && <p className="px-3 py-2 text-inline-meta text-ink-2 m-0">Showing {matches.length} of {found.length} · type to narrow</p>}
            </Command.List>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </Command>
  );
}

/** The row that keeps the text as typed; task rows use the task's id, so this can never collide. */
const TYPED = 'typed';
